"use client"
/* eslint-disable @next/next/no-img-element -- two small fixed-size brand marks
   (the Supabase-stored Fridge.com icon through the storage proxy, and Google's
   G from public/); next/image adds nothing at 28px. */

import { useEffect, useState } from "react"
import posthog from "posthog-js"
import { X } from "lucide-react"
import { PreferredSourceLink } from "@/components/chrome/preferred-source-link"
import {
  PROMPT_DELAY_MS,
  PROMPT_MIN_WIDTH,
  PROMPT_STORAGE_KEY,
  assignBucket,
  isTopArticle,
  parsePromptState,
  serializePromptState,
  shouldShowPrompt,
  type PromptState,
} from "@/lib/preferred-source-prompt"

/**
 * FRIDGE-611 — the self-showing preferred-source prompt (owner "Agree",
 * 09/24/2026: it should appear on its own, with the real logos).
 *
 * A small dismissible slide-in on the week's ten busiest articles (the live
 * /api/blogs/trending feed: PostHog pageviews over the last 7 days, so the set
 * rotates on its own). Desktop-weighted and never a full-screen interstitial;
 * it appears only after the reader scrolls, at most once per visitor per
 * cooldown, and never on top of the newsletter popup. Half of eligible
 * visitors are a control group that never sees it, so the lift is measured,
 * not assumed. The button is the same PreferredSourceLink every article
 * already carries: Google's own control makes the choice — nothing opens by
 * itself.
 */

const FRIDGE_ICON = "/company-logos/Fridge.com-Favicon-Icon.png"
const GOOGLE_G = "/icons/social/google.svg"

function readState(): PromptState | null {
  try {
    return parsePromptState(window.localStorage.getItem(PROMPT_STORAGE_KEY))
  } catch {
    return null
  }
}

function writeState(state: PromptState) {
  try {
    window.localStorage.setItem(PROMPT_STORAGE_KEY, serializePromptState(state))
  } catch {
    /* private mode: the prompt still behaves, it just may show again */
  }
}

function capture(event: string, props: Record<string, unknown>) {
  posthog?.capture?.(event, props)
}

export function PreferredSourcePrompt() {
  const [open, setOpen] = useState(false)
  const [bucket, setBucket] = useState<PromptState["bucket"] | null>(null)

  useEffect(() => {
    if (window.innerWidth < PROMPT_MIN_WIDTH) return
    let cancelled = false
    let timer: number | undefined

    const run = async () => {
      let state = readState()
      if (!state) {
        state = { bucket: assignBucket(Math.random()) }
        writeState(state)
      }
      let top: string[] = []
      try {
        const res = await fetch('/api/blogs/trending?limit=10', { cache: "no-store" })
        const data = (await res.json()) as { source?: string; articles?: Array<{ slug?: string; url?: string }> }
        // Only real traffic decides; the feed's non-traffic fallback never does.
        if (data.source !== "posthog") return
        top = (data.articles ?? []).map((a) => a.url ?? (a.slug ? `/blogs/news/${a.slug}` : "")).filter(Boolean)
      } catch {
        return
      }
      if (cancelled || !isTopArticle(window.location.pathname, top)) return
      capture("preferred_source_prompt_eligible", { bucket: state.bucket, path: window.location.pathname })
      setBucket(state.bucket)
      if (!shouldShowPrompt(state, Date.now())) return
      // Never stack with the newsletter popup or any modal overlay.
      if (document.querySelector('[data-testid="newsletter-capture-popup"], [aria-modal="true"], dialog[open]')) return
      writeState({ ...state, shownAt: Date.now() })
      capture("preferred_source_prompt_shown", { bucket: state.bucket, path: window.location.pathname })
      setOpen(true)
    }

    const onScroll = () => {
      if (window.scrollY < window.innerHeight * 0.5) return
      window.removeEventListener("scroll", onScroll)
      timer = window.setTimeout(() => { void run() }, PROMPT_DELAY_MS)
    }
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => {
      cancelled = true
      window.removeEventListener("scroll", onScroll)
      if (timer) window.clearTimeout(timer)
    }
  }, [])

  if (!open || bucket !== "prompt") return null

  const dismiss = () => {
    const state = readState()
    if (state) writeState({ ...state, dismissedAt: Date.now() })
    capture("preferred_source_prompt_dismissed", { bucket: "prompt", path: window.location.pathname })
    setOpen(false)
  }

  return (
    <div
      data-preferred-source-prompt
      role="dialog"
      aria-modal="false"
      aria-labelledby="pref-src-prompt-title"
      className="fixed bottom-6 left-6 z-[60] w-[340px] rounded-xl border border-gray-200 bg-white p-4 shadow-[0_22px_44px_-16px_rgba(16,24,40,0.28)]"
    >
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        className="absolute right-2 top-2 inline-flex h-9 w-9 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100"
      >
        <X className="h-4 w-4" />
      </button>
      <div className="mb-2 flex items-center gap-2" aria-hidden="true">
        <img src={FRIDGE_ICON} alt="" width={28} height={28} className="h-7 w-7" />
        <span className="text-gray-300">+</span>
        <img src={GOOGLE_G} alt="" width={24} height={24} className="h-6 w-6" />
      </div>
      <p id="pref-src-prompt-title" className="m-0 pr-8 text-[15px] font-bold leading-snug text-gray-900">
        See Fridge.com first in Google results
      </p>
      <p className="mb-3 mt-1 text-[13px] leading-snug text-gray-600">
        Add Fridge.com as a preferred source. Google shows its own choice box; you can change it any time.
      </p>
      <PreferredSourceLink
        surface="prompt"
        className="inline-flex h-10 items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 text-[13px] font-semibold text-gray-900 no-underline! hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#ff9951]"
      >
        <img src={GOOGLE_G} alt="" width={18} height={18} className="h-[18px] w-[18px]" />
        Add as a preferred source
      </PreferredSourceLink>
    </div>
  )
}
